// Copyright (c) 2025 embyt GmbH. All rights reserved.
// Author: Roman Morawek

/*
  This module contains shared type definitions, global for all applications.
  These types are used in both, the controller engine and the cloud backend or frontend.
*/

import { ControllerAlarmSource } from "./parameter";

// alarms triggered in cloud
export enum CloudAlarmSource {
  Offline = "Cloud_Offline", // allows clearing
  TrendMonitor = "Cloud_TrendMonitor", // allows clearing?
  OcppChargerOffline = "Cloud_ChargerOffline", // allows clearing
  OcppChargerFaulted = "Cloud_ChargerFaulted", // allows clearing
  OcppChargingInterrupted = "Cloud_ChargingInterrupted", // allows clearing
}

// all alarm sources, as stored in the cloud database; usable like an enum
export const AlarmSource = { ...ControllerAlarmSource, ...CloudAlarmSource };
export type AlarmSource = ControllerAlarmSource | CloudAlarmSource;

// this are the enum values as stored in the controller and cloud database
export enum AlarmState {
  Active = 0, // alarm is active and not acknowledged
  AckPending = 1, // alarm is inactive but not acknowledged
  AckdActive = 2, // alarm is active and acknowledged
  Ackd = 3, // alarm is inactive and acknowledged
}

// S optionally restricts the alarm source, e.g. to ControllerAlarmSource
export interface IAlarmEntry<S extends AlarmSource = AlarmSource> {
  source: S;
  alarmTimestamp: number; // s
  value: number;
  state: AlarmState;
  description?: string;
  clearTimestamp?: number; // s
  ackTimestamp?: number; // s
  ackSource?: string;
}

export enum DeviceType {
  LINX_120 = "LINX-120",
  LINX_153 = "LINX-153",
  LIOB_586 = "LIOB-586",
  EMBYT_PC = "embyt-pc",
  EMBYT_STRATO_3 = "embyt-strato-3", // Compute Module 3+ devices
  EMBYT_STRATO_4 = "embyt-strato-4", // Compute Module 4 devices
  EMBYT_STRATO = "embyt-strato", // kept as the fallback for other RPi devices
  EMBYT_EAP = "embyt-eap",
  VIRTUAL = "virtual",
  CLOUD_VM = "cloud-vm",
}

// true for any raspberry pi device variant
export function isStratoDevice(type: DeviceType | undefined) {
  return (
    type === DeviceType.EMBYT_STRATO ||
    type === DeviceType.EMBYT_STRATO_3 ||
    type === DeviceType.EMBYT_STRATO_4
  );
}

export type InputValueType = number | boolean | undefined;
export type OutputValueType = number | boolean | undefined;
